package br.com.vvrefeicoes.portal.feature.rmp

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.Checkbox
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.RmpApproval
import com.composables.icons.lucide.BadgeCheck
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.RotateCcw
import com.composables.icons.lucide.Save
import com.composables.icons.lucide.UserRoundPen
import kotlinx.serialization.json.JsonPrimitive
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rmp_admin_apt
import portalvv.shared.generated.resources.rmp_admin_hr_status
import portalvv.shared.generated.resources.rmp_admin_reason
import portalvv.shared.generated.resources.rmp_admin_reopen
import portalvv.shared.generated.resources.rmp_admin_reopen_hint
import portalvv.shared.generated.resources.rmp_admin_reopen_title
import portalvv.shared.generated.resources.rmp_admin_replace
import portalvv.shared.generated.resources.rmp_admin_replace_title
import portalvv.shared.generated.resources.rmp_admin_save
import portalvv.shared.generated.resources.rmp_admin_section
import portalvv.shared.generated.resources.rmp_approval_approved
import portalvv.shared.generated.resources.rmp_approval_rejected
import portalvv.shared.generated.resources.rmp_decision_cancel

/** Campos da substituição do candidato (chave da API → rótulo). */
private val REPLACEMENT_FIELDS =
    listOf(
        "nome" to "Nome do novo candidato *",
        "cargo" to "Cargo",
        "turno" to "Turno",
        "salario" to "Salário (R$)",
        "gratificacao" to "Gratificação (R$)",
    )

/**
 * Ações depois do fluxo (como no sistema antigo): a situação do RH ("Apto") e, para quem pode, marcar como Apto,
 * substituir o candidato da admissão concluída e (Admin) reabrir o RMP removendo decisões do histórico.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun RmpAdminActions(
    approval: RmpApproval,
    controller: RmpApprovalController,
    onChanged: () -> Unit,
) {
    var reopening by remember(approval.request.id) { mutableStateOf(false) }
    var replacing by remember(approval.request.id) { mutableStateOf(false) }
    val hasActions = approval.canMarkApt || approval.canReplaceCandidate || approval.canReopen
    if (approval.hrStatus == null && !hasActions) return
    HorizontalDivider()
    Text(text = stringResource(Res.string.rmp_admin_section), style = MaterialTheme.typography.titleMedium)
    approval.hrStatus?.let { status ->
        Text(
            text = stringResource(Res.string.rmp_admin_hr_status, status),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.primary,
        )
    }
    FlowRow(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8), verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        if (approval.canMarkApt) {
            AppButton(
                text = stringResource(Res.string.rmp_admin_apt),
                onClick = { controller.markApt(onChanged) },
                enabled = !controller.deciding,
                variant = PortalButtonVariant.Outlined,
                leadingIcon = Lucide.BadgeCheck,
            )
        }
        if (approval.canReplaceCandidate) {
            AppButton(
                text = stringResource(Res.string.rmp_admin_replace),
                onClick = { replacing = true },
                enabled = !controller.deciding,
                variant = PortalButtonVariant.Outlined,
                leadingIcon = Lucide.UserRoundPen,
            )
        }
        if (approval.canReopen) {
            AppButton(
                text = stringResource(Res.string.rmp_admin_reopen),
                onClick = { reopening = true },
                enabled = !controller.deciding,
                variant = PortalButtonVariant.Text,
                leadingIcon = Lucide.RotateCcw,
            )
        }
    }
    if (replacing) {
        ReplaceCandidateDialog(approval, controller, onDismiss = { replacing = false }) {
            replacing = false
            onChanged()
        }
    }
    if (reopening) {
        ReopenDialog(approval, controller, onDismiss = { reopening = false }) {
            reopening = false
            onChanged()
        }
    }
}

@Composable
private fun ReplaceCandidateDialog(
    approval: RmpApproval,
    controller: RmpApprovalController,
    onDismiss: () -> Unit,
    onSaved: () -> Unit,
) {
    val current = approval.request.data
    var values by remember {
        mutableStateOf(REPLACEMENT_FIELDS.associate { (key, _) -> key to ((current[key] as? JsonPrimitive)?.content ?: "") })
    }
    var reason by remember { mutableStateOf("") }
    AppModalCard(
        title =
            stringResource(
                Res.string.rmp_admin_replace_title,
                approval.request.number
                    ?.toString()
                    .orEmpty(),
            ),
        onDismiss = onDismiss,
    ) {
        controller.errorMessage?.let { message ->
            AppBanner(text = message, onDismiss = controller::dismissError, isError = true)
        }
        REPLACEMENT_FIELDS.forEach { (key, label) ->
            AppTextField(
                value = values[key].orEmpty(),
                onValueChange = { input ->
                    values = values + (key to if (key == "salario" || key == "gratificacao") maskCurrency(input) else input.take(255))
                },
                label = label,
                modifier = Modifier.fillMaxWidth(),
            )
        }
        AppTextField(
            value = reason,
            onValueChange = { reason = it.take(500) },
            label = stringResource(Res.string.rmp_admin_reason),
            singleLine = false,
            modifier = Modifier.fillMaxWidth(),
        )
        DialogButtons(controller, onDismiss) { controller.replaceCandidate(values, reason, onSaved) }
    }
}

@Composable
private fun ReopenDialog(
    approval: RmpApproval,
    controller: RmpApprovalController,
    onDismiss: () -> Unit,
    onSaved: () -> Unit,
) {
    var selected by remember { mutableStateOf(setOf<String>()) }
    AppModalCard(
        title =
            stringResource(
                Res.string.rmp_admin_reopen_title,
                approval.request.number
                    ?.toString()
                    .orEmpty(),
            ),
        onDismiss = onDismiss,
    ) {
        controller.errorMessage?.let { message ->
            AppBanner(text = message, onDismiss = controller::dismissError, isError = true)
        }
        Text(
            text = stringResource(Res.string.rmp_admin_reopen_hint),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        approval.approvals.forEach { entry ->
            val id = entry.id ?: return@forEach
            val decision =
                stringResource(
                    if (entry.decision == "REJEITADO") Res.string.rmp_approval_rejected else Res.string.rmp_approval_approved,
                )
            Row(
                modifier = Modifier.fillMaxWidth().clickable { selected = if (id in selected) selected - id else selected + id },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Checkbox(
                    checked = id in selected,
                    onCheckedChange = { selected = if (id in selected) selected - id else selected + id },
                )
                Column {
                    Text(
                        text = listOfNotNull(entry.stageLabel, decision).joinToString(" · "),
                        style = MaterialTheme.typography.labelLarge,
                    )
                    Text(
                        text = listOfNotNull(entry.decidedByName, formatBusinessDateTime(entry.decidedAt)).joinToString(" · "),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        }
        DialogButtons(controller, onDismiss, enabled = selected.isNotEmpty()) {
            controller.reopen(selected.toList(), onSaved)
        }
    }
}

@Composable
private fun DialogButtons(
    controller: RmpApprovalController,
    onDismiss: () -> Unit,
    enabled: Boolean = true,
    onSave: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12, Alignment.End),
    ) {
        AppButton(
            text = stringResource(Res.string.rmp_decision_cancel),
            onClick = onDismiss,
            enabled = !controller.deciding,
            variant = PortalButtonVariant.Text,
        )
        AppButton(
            text = stringResource(Res.string.rmp_admin_save),
            onClick = onSave,
            enabled = enabled && !controller.deciding,
            variant = PortalButtonVariant.Filled,
            leadingIcon = Lucide.Save,
        )
    }
}
